import { type FC, memo } from "react";
import type { EventListGridItem } from "src/http/types";
import type { Dates } from "src/types/shared/cookies";
import { mobileCalendarCard } from "src/website-apps/responsive/stubhub-performer/components/calendar/styles";
import { getCalendarDays } from "src/website-apps/responsive/stubhub-performer/components/calendar/utils";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { getMonthDateKey } from "src/website-apps/shared/utils/calendar";
import { MlbDateCardMobile } from "./MlbDateCardMobile";
import { MobileCalendarLoadingSkeleton } from "./MobileCalendarLoadingSkeleton";
import { MobileCalendarNoResults } from "./MobileCalendarNoResults";

interface Props {
  /** Months to render in the mobile calendar */
  months: Date[];
  /** Events organized by month-year key and day number */
  eventsByMonthAndDay: Record<string, Record<number, EventListGridItem[]>>;
  /** Selected dates from the date selector */
  dates: Maybe<Dates>;
  /** Performer's categoryId */
  categoryId: number;
  /** Loading state for calendar events */
  isUpdating: boolean;
  /** Handler to open notification modal for a specific event */
  openNotificationHandler: (eventId: number) => void;
  /** Resets filters when no results */
  resetFilters: () => void;
}

// Stable empty array to avoid new [] allocation that breaks memoization
const EMPTY_EVENTS: EventListGridItem[] = [];

export const MobileCalendar: FC<Props> = memo(
  ({ months, eventsByMonthAndDay, dates, categoryId, isUpdating, openNotificationHandler, resetFilters }) => {
    const today = useStableNow();

    if (months.length > 0) {
      return months.map((monthDate) => {
        const monthKey = getMonthDateKey(monthDate);
        const calendarDays = getCalendarDays(monthDate, dates, today);

        return (
          <div key={`${monthKey}-mobile`} className={mobileCalendarCard}>
            {calendarDays.map(({ key, day }) => (
              <MlbDateCardMobile
                key={key}
                events={day ? (eventsByMonthAndDay[monthKey]?.[day] ?? EMPTY_EVENTS) : EMPTY_EVENTS}
                categoryId={categoryId}
                loading={isUpdating}
                openNotificationHandler={openNotificationHandler}
              />
            ))}
          </div>
        );
      });
    }

    if (isUpdating) {
      return <MobileCalendarLoadingSkeleton count={6} />;
    }
    return <MobileCalendarNoResults resetFilters={resetFilters} />;
  },
);

MobileCalendar.displayName = "MobileCalendar";
